import { Language } from '../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../common/utils/format-utils.js'
import { Button } from '../button/button.js'
import './portal-timeline.css'

export interface TimelineEntry {
    id: number
    label: string
    sublabel?: string
    effectiveFrom: string
    effectiveTo: string | null
    isCurrent: boolean
    isFuture: boolean
    details?: { label: string; value: string }[]
    badge?: string
    badgeVariant?: 'current' | 'future' | 'past'
    /** Row actions (e.g. Edit + Delete), rendered as shared tertiary buttons. */
    actions?: { label: string; onAction: () => void }[]
}

export interface PortalTimelineProps {
    entries: TimelineEntry[]
    emptyMessage: string
    lang: Language
}

export const PortalTimeline = ({ entries, emptyMessage }: PortalTimelineProps) => {
    if (entries.length === 0) {
        return <div className="portal-timeline__empty">{emptyMessage}</div>
    }

    return (
        <div className="portal-timeline">
            {entries.map((entry, index) => {
                const dotClass = entry.isFuture
                    ? 'portal-timeline__dot--future'
                    : entry.isCurrent
                      ? 'portal-timeline__dot--current'
                      : 'portal-timeline__dot--past'

                return (
                    <div
                        key={entry.id}
                        className={`portal-timeline__entry ${index === entries.length - 1 ? 'portal-timeline__entry--last' : ''}`}
                    >
                        <div className="portal-timeline__track">
                            <div className={`portal-timeline__dot ${dotClass}`} />
                            {index < entries.length - 1 && (
                                <div className="portal-timeline__line" />
                            )}
                        </div>
                        <div className="portal-timeline__content">
                            <div className="portal-timeline__header">
                                <span className="portal-timeline__label">{entry.label}</span>
                                {entry.badge && (
                                    <span
                                        className={`portal-timeline__badge portal-timeline__badge--${entry.badgeVariant ?? 'past'}`}
                                    >
                                        {entry.badge}
                                    </span>
                                )}
                            </div>
                            {entry.sublabel && (
                                <div className="portal-timeline__sublabel">{entry.sublabel}</div>
                            )}
                            <div className="portal-timeline__date-range">
                                {formatDisplayDate(entry.effectiveFrom)}
                                {entry.effectiveTo
                                    ? ` → ${formatDisplayDate(entry.effectiveTo)}`
                                    : ' → ...'}
                            </div>
                            {entry.details && entry.details.length > 0 && (
                                <div className="portal-timeline__details">
                                    {entry.details.map((d, i) => (
                                        <div key={i} className="portal-timeline__detail">
                                            <span className="portal-timeline__detail-label">
                                                {d.label}:
                                            </span>{' '}
                                            <span className="portal-timeline__detail-value">
                                                {d.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {entry.actions && entry.actions.length > 0 && (
                                <div className="portal-timeline__actions">
                                    {entry.actions.map((a, i) => (
                                        <Button
                                            key={i}
                                            variant="tertiary"
                                            size="small"
                                            text={a.label}
                                            onClick={a.onAction}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
